import React, { useState, useEffect } from 'react';
import api, { errorText } from '../config';
import DigestView from '../components/DigestView';
import DDRequestList from '../components/DDRequestList';
import DataRoomPull from '../components/DataRoomPull';
import DDWorkstreams from '../components/DDWorkstreams';
import DealDocs from '../components/DealDocs';
import LboModel from '../components/LboModel';
import OperatingModel from '../components/OperatingModel';
import {
  FileText, Sparkles, Cpu, Download, Plus, X, ArrowLeft,
  ChevronRight, Building2, Calendar, AlertTriangle, AlertCircle,
  Trash2, Loader2, ArrowRight, CheckCircle2, Clock, Circle,
  TrendingUp, User, Paperclip, Briefcase, GitMerge, FileSearch,
  BrainCircuit, Wallet, ListChecks, Receipt, XCircle, HelpCircle, BookOpen, RefreshCw,
} from 'lucide-react';

const REPORT_TYPE_LABELS = {
  investment_memo: 'Investment Memo',
  quarterly_review: 'Quarterly Review',
  due_diligence: 'Due Diligence',
  market_analysis: 'Market Analysis',
  board_deck: 'Board Deck',
};

const STATUS_CONFIG = {
  completed: { label: 'Completed', badge: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  in_progress: { label: 'In Progress', badge: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  draft: { label: 'Draft', badge: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
  generated: { label: 'Generated', badge: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
};

const SECTION_ICONS = {
  'Executive Summary': FileText,
  'Financial Overview': TrendingUp,
  'Financial Performance': TrendingUp,
  'Preliminary Screening Summary': FileText,
  'Additional Diligence Required': AlertTriangle,
  'Strategic Recommendations & Value Creation': Sparkles,
  'Risk Assessment': AlertTriangle,
  'Revenue Analysis': TrendingUp,
  'Working Capital: AR/AP Aging & Vendor Spend': Wallet,
  'Uploaded Documents & AI Findings': Paperclip,
  'AI Analysis & Recommendations': BrainCircuit,
  'Value Creation Checklist (rule-based)': ListChecks,
  'Billing & Accounts Receivable': Receipt,
};

/* Fetches a protected PDF route with the auth token and saves it locally. */
function PdfDownloadButton({ url, filename }) {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const objectUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = objectUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={save} disabled={busy} className="btn-secondary shrink-0 text-xs">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}PDF
    </button>
  );
}

function ReportDetail({ report, onBack, onDownload, onViewPipeline, onConvertToPortco, isConverted, convertLoading }) {
  const typeLabel = REPORT_TYPE_LABELS[report.report_type] || report.report_type;
  const cfg = STATUS_CONFIG[report.status] || STATUS_CONFIG.draft;
  const isDeal = report.portco_id && report.portco_id.startsWith('deal-');
  const isDiligenceReport = report.report_id && report.report_id.startsWith('rpt-dil-');

  return (
    <div className="page-container w-full max-w-6xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 transition mb-4">
        <ArrowLeft className="w-4 h-4" />Back to Active Deals
      </button>

      {/* Report header */}
      <div className="card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-xl font-bold text-navy-900">{typeLabel}</h2>
              <span className={`badge ${cfg.badge}`}>
                <span className={`status-dot ${cfg.dot} mr-1`} />{cfg.label}
              </span>
            </div>
            <p className="text-sm text-slate-500">{report.executive_summary}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isDeal && (
              <button onClick={() => onViewPipeline(report.portco_id)} className="btn-secondary text-xs">
                <Cpu className="w-3.5 h-3.5" />Pipeline
              </button>
            )}
            {report.pdf_url && (
              <PdfDownloadButton url={report.pdf_url} filename={`Caply_${report.report_id}.pdf`} />
            )}
            <button onClick={() => onDownload(report.report_id)} className="btn-primary shrink-0">
              <Download className="w-4 h-4" />Download
            </button>
          </div>
        </div>

        {/* Stage progression for deal reports */}
        {isDeal && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />Deal Submitted
            </div>
            <ChevronRight className="w-3 h-3 text-slate-300" />
            <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />Pipeline Run
            </div>
            <ChevronRight className="w-3 h-3 text-slate-300" />
            <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />Report Generated
            </div>
            <ChevronRight className="w-3 h-3 text-slate-300" />
            {isConverted ? (
              <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />Portfolio Company
              </div>
            ) : (
              <button
                onClick={onConvertToPortco}
                disabled={convertLoading}
                className="flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 bg-brand-50 hover:bg-brand-100 px-3 py-1.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {convertLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" />Converting...</>
                ) : (
                  <><Building2 className="w-3.5 h-3.5" />Convert to Portco</>
                )}
              </button>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />{report.portco_name || report.portco_id}</span>
          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{report.period || 'N/A'}</span>
          <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />{report.report_id}</span>
        </div>
      </div>

      {/* Report sections */}
      {report.sections && report.sections.map((section, idx) => {
        const SectionIcon = SECTION_ICONS[section.title] || FileText;
        const isRecs = section.title.includes('Value Creation') || section.kind === 'ai';

        // ── Tiles: key metrics as a grid of stat cards ──
        if (section.kind === 'tiles') {
          return (
            <div key={idx} className="card">
              <div className="card-header">
                <h3 className="card-title"><SectionIcon className="w-5 h-5 text-brand-600" />{section.title}</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {(section.data_tables || []).map((tile, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg px-3 py-2.5">
                    <div className="text-xs text-slate-500">{tile.metric || Object.keys(tile)[0]}</div>
                    <div className="font-semibold text-navy-900 text-sm mt-0.5">{tile.value ?? Object.values(tile)[0]}</div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{section.content}</p>
            </div>
          );
        }

        // ── Digests: every uploaded file with its analysis ──
        if (section.kind === 'digests') {
          return (
            <div key={idx} className="card">
              <div className="card-header">
                <h3 className="card-title"><SectionIcon className="w-5 h-5 text-brand-600" />{section.title}</h3>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mb-4">{section.content}</p>
              <div className="space-y-2">
                {(section.artifacts || []).map((dom) => (
                  <div key={dom.domain} className="bg-slate-50 rounded-lg px-3 py-2">
                    <div className="font-medium text-navy-900 text-xs mb-1">{dom.domain}</div>
                    {dom.files.map((fa, i) => (
                      <div key={i} className={i > 0 ? 'mt-2 pt-2 border-t border-slate-200' : ''}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-xs text-slate-700 font-medium truncate">{fa.filename}</span>
                          <span className="text-xs text-slate-500">{fa.status === 'processed' ? 'analyzed' : fa.status}</span>
                        </div>
                        <div className="mt-1.5 rounded-lg bg-white border border-slate-200 px-3 py-2">
                          <DigestView text={String(fa.digest || '').slice(0, 1600)} />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // ── AI analysis: narrative + prioritized recommendations with reasoning ──
        if (section.kind === 'ai') {
          const ai = section.ai || {};
          const prStyle = p => p === 'High' ? 'bg-rose-50 text-rose-700 border-rose-200'
            : p === 'Medium' ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-slate-100 text-slate-600 border-slate-200';
          return (
            <div key={idx} className="card border-brand-200">
              <div className="card-header">
                <h3 className="card-title"><SectionIcon className="w-5 h-5 text-brand-600" />{section.title}</h3>
                {ai.recommendations && (
                  <span className="text-xs font-medium text-brand-700 bg-brand-50 rounded-full px-2.5 py-1">AI-generated · grounded in your uploaded documents</span>
                )}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line mb-4">{section.content}</p>

              {(ai.recommendations || []).length > 0 && (
                <div className="grid gap-3 md:grid-cols-2 mb-4">
                  {ai.recommendations.map((rec, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{rec.area}</span>
                        <span className={`text-xs font-semibold rounded-full border px-2 py-0.5 ${prStyle(rec.priority)}`}>{rec.priority}</span>
                      </div>
                      <div className="text-sm font-semibold text-navy-900 mb-1">{rec.recommendation}</div>
                      {rec.reasoning && <p className="text-xs text-slate-600 leading-relaxed">{rec.reasoning}</p>}
                    </div>
                  ))}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                {(ai.risks || []).length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Risks flagged in the data</div>
                    <ul className="space-y-1.5">
                      {ai.risks.map((r, i) => (
                        <li key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">{r}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(ai.data_gaps || []).length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Ask for this next</div>
                    <ul className="space-y-1.5">
                      {ai.data_gaps.map((g, i) => (
                        <li key={i} className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">{g}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        }

        // ── Default: prose + data table ──
        return (
          <div key={idx} className="card">
            <div className="card-header">
              <h3 className="card-title">
                <SectionIcon className={`w-5 h-5 ${isRecs ? 'text-emerald-600' : 'text-brand-600'}`} />
                {section.title}
              </h3>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mb-4 whitespace-pre-line">{section.content}</p>

            {section.data_tables && section.data_tables.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      {Object.keys(section.data_tables[0]).map(k => (
                        <th key={k} className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 pb-2 pr-4">
                          {k.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.data_tables.map((row, i) => (
                      <tr key={i} className="border-t border-slate-200">
                        {Object.values(row).map((v, j) => {
                          const valStr = String(v);
                          const isPriority = k => k === 'priority';
                          const key = Object.keys(row)[j];
                          return (
                            <td key={j} className="py-2 pr-4 text-slate-700 font-medium">
                              {isPriority(key) && valStr === 'High' ? (
                                <span className="inline-flex items-center gap-1 text-red-600">
                                  <AlertTriangle className="w-3.5 h-3.5" />High
                                </span>
                              ) : isPriority(key) && valStr === 'Medium' ? (
                                <span className="inline-flex items-center gap-1 text-amber-600">{valStr}</span>
                              ) : isPriority(key) && valStr === 'Low' ? (
                                <span className="inline-flex items-center gap-1 text-slate-500">{valStr}</span>
                              ) : valStr}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// AgentPipeline component (embedded)
// ═══════════════════════════════════════════════════════════════

const AGENT_DOMAINS = [
  { domain: 'cim', label: 'CIM / Company Overview', icon: BookOpen, accept: '.pdf,.docx,.pptx,.txt,.md', optional: true },
  { domain: 'financials', label: 'Financial Statements', icon: TrendingUp, accept: '.xlsx,.xls,.csv,.pdf' },
  { domain: 'hr', label: 'HR / Headcount', icon: User, accept: '.xlsx,.xls,.csv,.pdf' },
  { domain: 'billing', label: 'Billing / AR', icon: FileText, accept: '.xlsx,.xls,.csv,.pdf' },
  { domain: 'crm', label: 'Sales Pipeline / CRM', icon: Building2, accept: '.xlsx,.xls,.csv,.pdf,.pptx,.docx' },
];

function labelFor(domain) {
  return AGENT_DOMAINS.find(d => d.domain === domain)?.label || domain;
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

async function downloadFile(dealId, domain, fileIndex, filename) {
  try {
    const resp = await api.get(
      `/api/intake/deals/${dealId}/uploads/${domain}/${fileIndex}/download`,
      { responseType: 'blob', timeout: 60000 },
    );
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Download failed:', err);
    alert('Download failed — file may no longer exist.');
  }
}

function AgentPipeline({ dealId, onComplete }) {
  const [uploads, setUploads] = useState({});
  const [processing, setProcessing] = useState(null);
  const [deal, setDeal] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [dupMatches, setDupMatches] = useState(null);
  const [merging, setMerging] = useState(false);
  const [mergeNotice, setMergeNotice] = useState(null);
  const [mergeListOpen, setMergeListOpen] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Deal Review sections as horizontal tabs (all stay mounted; hidden ones
  // keep their state so unsaved edits survive tab switches)
  const [tab, setTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState(null);
  const [analysisVersion, setAnalysisVersion] = useState(0);
  const TABS = [
    { id: 'overview', label: 'Overview', hint: 'The deal snapshot: key numbers, editable details, and the whole-deal report.' },
    { id: 'requests', label: 'Data Requirements', hint: 'The counterparty request list — track what you asked for and what has arrived.' },
    { id: 'files', label: 'Diligence Files', hint: 'Upload financials, HR, billing, and CRM data — Caply analyzes every file.' },
    { id: 'workstreams', label: 'Workstreams', hint: 'Structured diligence across Commercial, Financial, Management, and Legal.' },
    { id: 'docs', label: 'Documents & Gates', hint: 'Phase gates and the IOI, LOI, and IC memo drafts.' },
    { id: 'lbo', label: 'LBO Model', hint: 'Returns, price sensitivity, and case comparison from the deal numbers.' },
    { id: 'opmodel', label: 'Operating Model', hint: 'How EBITDA grows from here — organic, M&A add-ons, and de novo builds.' },
  ];

  const loadDeal = () => api.get(`/api/intake/deals/${dealId}`).then(r => setDeal(r.data)).catch(() => {});

  // Refresh analysis & reports: re-reads every uploaded file, regenerates the
  // deal report, and bumps a signal so the mounted tabs (workstreams, files)
  // reload with the new findings — one clear action for business users.
  const refreshAnalysis = async () => {
    setRefreshing(true);
    setRefreshNote(null);
    try {
      const r = await api.post(`/api/intake/deals/${dealId}/reanalyze-uploads`);
      const reanalyzed = typeof r.data?.reanalyzed === 'number' ? r.data.reanalyzed : null;
      const serverMsg = r.data?.message || '';
      let reportOk = false;
      if (reanalyzed) {
        try {
          const g = await api.post(`/api/reports/${dealId}/generate-from-diligence`);
          reportOk = !!g.data?.report_id;
          if (reportOk) await fetchReports();
        } catch { /* report regen is best-effort; the analysis still refreshed */ }
      }
      await Promise.all([loadDeal(), refreshUploads()]);
      setAnalysisVersion(v => v + 1);
      setRefreshNote({ reanalyzed, serverMsg, reportOk });
    } catch (e) {
      setRefreshNote({ error: errorText(e, 'Refresh failed — nothing was changed.') });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    api.get(`/api/intake/deals/${dealId}`).then(r => setDeal(r.data)).catch(() => {});
    // Also load existing uploads
    api.get(`/api/intake/deals/${dealId}/uploads`).then(r => {
      if (r.data?.uploads) setUploads(r.data.uploads);
    }).catch(() => {});
    // Duplicate detector: another active deal with the same name → offer a merge
    api.get(`/api/intake/deals/${dealId}`)
      .then(r => r.data?.companyName)
      .then(name => {
        if (!name) return;
        return api.get('/api/intake/deals/lookup', { params: { company: name } })
          .then(r => {
            const others = (r.data?.matches || []).filter(m => m.id !== dealId);
            if (others.length) setDupMatches(others);
          }).catch(() => {});
      }).catch(() => {});
  }, [dealId]);

  // ── Edit-in-place: update core deal details without re-creating the deal ──
  const openEdit = () => {
    setEditForm({
      sector: deal?.sector || '',
      stage: deal?.stage || '',
      annualRevenue: deal?.annualRevenue ?? '',
      ebitdaMargin: deal?.ebitdaMargin ?? '',
      targetValuation: deal?.targetValuation ?? '',
      leadPartner: deal?.leadPartner || '',
      notes: deal?.notes || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const patch = {};
      ['sector', 'stage', 'leadPartner', 'notes'].forEach(k => {
        if (editForm[k] !== '' && editForm[k] !== null) patch[k] = editForm[k];
      });
      ['annualRevenue', 'ebitdaMargin', 'targetValuation'].forEach(k => {
        const v = editForm[k];
        if (v !== '' && v !== null) patch[k] = Number(v);
      });
      if (patch.stage === '' ) patch.stage = null;
      await api.patch(`/api/intake/deals/${dealId}`, patch);
      await loadDeal();
      setEditOpen(false);
    } catch (err) {
      alert(errorText(err, 'Could not save deal details.'));
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Merge a duplicate INTO this deal (this deal survives) ──
  const mergeDuplicate = async (sourceId) => {
    setMerging(true);
    try {
      const r = await api.post(`/api/intake/deals/${dealId}/merge`, { source_deal_id: sourceId });
      const rep = r.data || {};
      const parts = [];
      if (rep.fields_filled?.length) parts.push(`${rep.fields_filled.length} details filled in`);
      if (rep.request_list) parts.push('request list combined');
      if (rep.workstreams === 'moved') parts.push('workstreams moved over');
      if (rep.docs_moved?.length) parts.push(`${rep.docs_moved.length} document(s) moved`);
      setMergeNotice(`Merged the other entry into this deal${parts.length ? ' — ' + parts.join(', ') : ''}.`);
      setDupMatches(null);
      setMergeListOpen(false);
      await loadDeal();
      window.location.reload(); // refresh all Deal Review panels (list, workstreams, docs)
    } catch (err) {
      alert(errorText(err, 'Merge failed.'));
    } finally {
      setMerging(false);
    }
  };

  // ── View report: whole-deal snapshot at any lifecycle point (uploads, financials, DD entries) ──
  const openReport = async () => {
    setReportOpen(true);
    setReportLoading(true);
    const out = { artifacts: null, requests: null, progress: null, findingsText: '' };
    try {
      const r = await api.post(`/api/dd/deals/${dealId}/findings-summary`);
      const snap = r.data || {};
      out.artifacts = (snap.artifact_findings && Object.keys(snap.artifact_findings).length) ? snap.artifact_findings : null;
      out.requests = snap.request_list || null;
      out.progress = snap.progress || null;
      out.findingsText = snap.summary?.text || snap.summary?.summary_text || '';
    } catch { /* no DD workspace yet — deal basics still render */ }
    if (!out.artifacts || !Object.keys(out.artifacts).length) {
      out.artifacts = deal?.agent_results?.diligence_results || {};
    }
    setReportData(out);
    setReportLoading(false);
  };

  // ── Merge any other deal into this one (not just same-name duplicates) ──
  const openMergeList = async () => {
    setMergeListOpen(true);
    setMergeCandidates(null);
    try {
      const r = await api.get('/api/intake/deals');
      setMergeCandidates((r.data || []).filter(d => d.id !== dealId));
    } catch {
      setMergeCandidates([]);
    }
  };

  const refreshUploads = async () => {
    try {
      const r = await api.get(`/api/intake/deals/${dealId}/uploads`);
      if (r.data?.uploads) setUploads(r.data.uploads);
    } catch { /* silent */ }
  };

  const handleUpload = async (domain, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('domain', domain);
    setProcessing(domain);
    try {
      await api.post(`/api/intake/deals/${dealId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      // Refresh deal to see updated agent results
      const r = await api.get(`/api/intake/deals/${dealId}`);
      setDeal(r.data);
      // Refresh the file list from the server (real filename + index)
      await refreshUploads();
    } catch (err) {
      console.error(`Upload failed for ${domain}:`, err);
      alert(`Upload failed for ${labelFor(domain)}: ${err.response?.data?.detail || err.message}`);
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteFile = async (domain, fileIndex) => {
    try {
      await api.delete(`/api/intake/deals/${dealId}/uploads/${domain}/${fileIndex}`);
      await refreshUploads();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const diligenceResults = deal?.agent_results?.diligence_results || {};
  const allDone = AGENT_DOMAINS.filter(d => !d.optional).every(d => diligenceResults[d.domain]?.status === 'processed');

  const fmtMoney = (v) => (v === null || v === undefined) ? '—' : `$${Number(v).toLocaleString()}`;
  const editField = (k, label, type = 'text') => (
    <label key={k} className="block">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <input
        type={type}
        value={editForm[k]}
        onChange={(e) => setEditForm(f => ({ ...f, [k]: e.target.value }))}
        className="form-input mt-0.5"
      />
    </label>
  );

  return (
    <div className="space-y-3">
      {/* Duplicate entry — merge to keep all work in one place */}
      {dupMatches && (
        <div className="card border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Another deal named &ldquo;{deal?.companyName}&rdquo; already exists.</span>{' '}
              Merge it into this deal to combine the details and keep all your work in one place.
              <ul className="mt-1 text-xs text-amber-800">
                {dupMatches.map(m => (
                  <li key={m.id}>
                    • Created {new Date(m.created_at).toLocaleDateString()} ·{' '}
                    {m.has_financials ? 'has financial details' : 'no financial details yet'}
                    {m.deal_phase ? ` · phase: ${m.deal_phase}` : ''}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => mergeDuplicate(dupMatches[0].id)}
                disabled={merging}
                className="btn-primary text-xs py-1.5 px-3"
              >
                {merging ? 'Merging…' : 'Merge into this deal'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="card p-0">
        <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2.5 border-b border-slate-200">
          <div className="text-xs text-slate-500 min-w-0">
            Everything about <strong className="text-navy-900">{deal?.companyName || 'this deal'}</strong> lives in the tabs below — nothing is lost when you switch.
          </div>
          <div className="flex items-center gap-2">
            {refreshNote && !refreshNote.error && (
              <button onClick={() => setTab('docs')} className="text-xs text-emerald-700 hover:underline whitespace-nowrap">
                Open Documents &amp; Gates →
              </button>
            )}
            <button
              onClick={refreshAnalysis}
              disabled={refreshing}
              title="Re-reads every uploaded file and regenerates the deal report so IOI, LOI, and IC memo drafts use the latest numbers"
              className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 disabled:opacity-50"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {refreshing ? 'Refreshing…' : 'Refresh analysis & reports'}
            </button>
          </div>
        </div>
        <div className="flex gap-1 overflow-x-auto px-2">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 -mb-px transition ${
                tab === t.id
                  ? 'border-brand-500 text-navy-900'
                  : 'border-transparent text-slate-500 hover:text-navy-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50/70 border-t border-slate-100">
          {TABS.find(t => t.id === tab)?.hint}
        </div>
        {refreshNote && (
          <div className={`px-3 py-2 text-xs border-t ${refreshNote.error ? 'bg-rose-50 text-rose-700 border-rose-100' : 'bg-emerald-50 text-emerald-800 border-emerald-100'}`}>
            {refreshNote.error
              ? refreshNote.error
              : refreshNote.reanalyzed
                ? `Analysis refreshed from ${refreshNote.reanalyzed} file${refreshNote.reanalyzed === 1 ? '' : 's'}${refreshNote.reportOk ? ' and the deal report was regenerated' : ''}. Regenerate the IOI / LOI / IC memo in Documents & Gates to fold in the new numbers.`
                : `${refreshNote.serverMsg || 'No uploaded files to re-analyze yet.'} Add files in Diligence Files, then refresh.`}
          </div>
        )}
      </div>
      <div className={tab === 'overview' ? 'space-y-3' : 'hidden space-y-3'}>
      {/* Deal details — edit core parameters any time; drafts use the latest values */}
      <div className="card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-navy-900 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-brand-600" /> Deal details
            </h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
              <span>Sector: <strong>{deal?.sector || '—'}</strong></span>
              <span>Stage: <strong>{deal?.stage || '—'}</strong></span>
              <span>Revenue: <strong>{fmtMoney(deal?.annualRevenue)}</strong></span>
              <span>EBITDA margin: <strong>{deal?.ebitdaMargin != null ? deal.ebitdaMargin + '%' : '—'}</strong></span>
              <span>Target valuation: <strong>{fmtMoney(deal?.targetValuation)}</strong></span>
              <span>Lead: <strong>{deal?.leadPartner || '—'}</strong></span>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={openReport} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5">
              <FileSearch className="w-3.5 h-3.5" /> View report
            </button>
            <button onClick={openEdit} className="btn-secondary text-xs py-1.5 px-3">
              Update details
            </button>
            <button onClick={openMergeList} className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5">
              <GitMerge className="w-3.5 h-3.5" /> Merge another deal
            </button>
          </div>
        </div>
        {editOpen && editForm && (
          <form onSubmit={saveEdit} className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3">
            {editField('sector', 'Sector')}
            {editField('stage', 'Stage (early / growth / mature / pre_exit)')}
            {editField('annualRevenue', 'Annual revenue ($)', 'number')}
            {editField('ebitdaMargin', 'EBITDA margin (%)', 'number')}
            {editField('targetValuation', 'Target valuation ($)', 'number')}
            {editField('leadPartner', 'Lead partner')}
            <label className="block col-span-2 md:col-span-4">
              <span className="text-xs font-medium text-slate-500">Notes</span>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                className="form-input mt-0.5"
                rows={2}
              />
            </label>
            <div className="col-span-2 md:col-span-4 flex gap-2">
              <button type="submit" disabled={savingEdit} className="btn-primary text-xs py-1.5 px-3">
                {savingEdit ? 'Saving…' : 'Save details'}
              </button>
              <button type="button" onClick={() => setEditOpen(false)} className="btn-secondary text-xs py-1.5 px-3">
                Cancel
              </button>
            </div>
          </form>
        )}
        {mergeNotice && (
          <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
            {mergeNotice}
          </div>
        )}
      </div>

      </div>

      {/* Sections below live under their own tabs; hidden (not unmounted)
          so component state survives tab switches */}
      <div className={tab === 'lbo' ? '' : 'hidden'}>
        <LboModel dealId={dealId} deal={deal} />
      </div>
      <div className={tab === 'opmodel' ? '' : 'hidden'}>
        <OperatingModel dealId={dealId} deal={deal} />
      </div>
      <div className={tab === 'docs' ? '' : 'hidden'}>
        <DealDocs dealId={dealId} />
      </div>
      <div className={tab === 'requests' ? '' : 'hidden'}>
        <DDRequestList dealId={dealId} />
      </div>
      <div className={tab === 'workstreams' ? '' : 'hidden'}>
        <DDWorkstreams dealId={dealId} dealName={deal?.companyName} refreshSignal={analysisVersion} />
      </div>
      <div className={tab === 'files' ? 'space-y-3' : 'hidden space-y-3'}>
      <DataRoomPull dealId={dealId} />
      {AGENT_DOMAINS.map(({ domain, label, icon: Icon, accept }) => {
        const result = diligenceResults[domain] || {};
        const isDone = result.status === 'processed';
        const isProcessing = processing === domain;
        const files = uploads[domain] || [];

        return (
          <div key={domain} className="card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`p-2 rounded-lg ${isDone ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                  <Icon className={`w-4 h-4 ${isDone ? 'text-emerald-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-navy-900">{label}</p>
                  {isDone && result.summary && (
                    <p className="text-xs text-emerald-600 mt-0.5">{result.summary.slice(0, 80)}</p>
                  )}
                  {domain === 'cim' && !files.length && !isDone && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      The first document of a deal — upload the seller's CIM for an early read, before any financials exist.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isDone ? (
                  <span className="badge bg-emerald-50 text-emerald-700">
                    <CheckCircle2 className="w-3 h-3 mr-1" />Analyzed
                  </span>
                ) : isProcessing ? (
                  <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                ) : (
                  <Circle className="w-4 h-4 text-slate-300" />
                )}
              </div>
            </div>

            {/* Uploaded files */}
            {files.length > 0 && (
              <div className="space-y-1 mb-2">
                {files.map((f, fi) => (
                  <div key={fi} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-1.5 gap-2">
                    <span className="text-slate-600 truncate" title={f.filename}>
                      <Paperclip className="w-3 h-3 inline-block mr-1 -mt-0.5 text-emerald-500" />
                      {f.filename}
                    </span>
                    <span className="flex items-center gap-1 shrink-0">
                      {!!f.size_bytes && <span className="text-slate-400">{fmtSize(f.size_bytes)}</span>}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          downloadFile(dealId, domain, f.file_index ?? fi, f.filename);
                        }}
                        className="text-slate-400 hover:text-brand-600"
                        title="Download file"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                      <button
                        onClick={() => handleDeleteFile(domain, f.file_index ?? fi)}
                        className="text-slate-400 hover:text-red-500"
                        title="Delete file"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Upload button */}
            <label className="flex items-center justify-center gap-2 text-xs font-medium text-slate-500 hover:text-brand-600 border border-dashed border-slate-200 hover:border-brand-300 rounded-lg py-2 cursor-pointer transition">
              <input
                type="file"
                accept={accept}
                className="hidden"
                onChange={e => e.target.files[0] && handleUpload(domain, e.target.files[0])}
              />
              {isProcessing ? 'Processing...' : files.length > 0 ? 'Re-upload / Replace' : 'Upload Document'}
            </label>
          </div>
        );
      })}

      {/* Generate Report button */}
      {allDone && (
        <button onClick={() => onComplete(dealId)} className="btn-primary w-full">
          <Sparkles className="w-4 h-4" />Generate AI Report from Findings
        </button>
      )}
      </div>

      {/* ── View report modal: live whole-deal snapshot, available at any lifecycle point ── */}
      {reportOpen && (
        <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !reportLoading && setReportOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-xl z-10">
              <div>
                <h3 className="text-lg font-bold text-navy-900">{deal?.companyName} — Deal Report</h3>
                <p className="text-xs text-slate-500 mt-0.5">A live snapshot of everything captured so far. It updates as you upload documents and progress the deal.</p>
              </div>
              <button onClick={() => setReportOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-6 text-sm">
              {reportLoading ? (
                <div className="flex items-center gap-2 text-slate-500 py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Building the report…</div>
              ) : (<>
                {/* Financials & deal parameters */}
                <section>
                  <h4 className="font-semibold text-navy-900 mb-2 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-brand-600" /> Financials &amp; deal parameters</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                    {[
                      ['Revenue', fmtMoney(deal?.annualRevenue)],
                      ['EBITDA margin', deal?.ebitdaMargin != null ? deal.ebitdaMargin + '%' : '—'],
                      ['Est. EBITDA', fmtMoney(deal?.agent_results?.financial_overview?.estimated_ebitda)],
                      ['Target valuation', fmtMoney(deal?.targetValuation)],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-slate-50 rounded-lg px-3 py-2">
                        <div className="text-xs text-slate-500">{label}</div>
                        <div className="font-semibold text-navy-900 text-sm mt-0.5">{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                    <span>Phase: <strong>{deal?.deal_phase || 'not set'}</strong></span>
                    <span>Stage: <strong>{deal?.stage || '—'}</strong></span>
                    <span>Sector: <strong>{deal?.sector || '—'}</strong></span>
                    <span>Lead: <strong>{deal?.leadPartner || '—'}</strong></span>
                  </div>
                  {deal?.notes && <p className="mt-2 text-xs text-slate-500 italic">{deal.notes}</p>}
                </section>

                {/* Latest uploaded-document analysis (incl. AP/AR via billing uploads) */}
                <section>
                  <h4 className="font-semibold text-navy-900 mb-2 flex items-center gap-1.5"><Paperclip className="w-4 h-4 text-brand-600" /> Uploaded documents &amp; latest analysis</h4>
                  {Object.keys(reportData?.artifacts || {}).length === 0 ? (
                    <p className="text-xs text-slate-400">No documents uploaded or analyzed yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(reportData.artifacts).map(([domain, res]) => {
                        // Every analyzed file for the domain — latest first, then earlier uploads
                        const files = [res, ...(res.history || [])].filter(f => f && (f.digest || f.summary));
                        return (
                          <div key={domain} className="bg-slate-50 rounded-lg px-3 py-2">
                            <div className="font-medium text-navy-900 text-xs mb-1">{labelFor(domain)}</div>
                            {files.length === 0 && (
                              <p className="text-xs text-slate-400">No analysis yet.</p>
                            )}
                            {files.map((fa, i) => (
                              <div key={i} className={i > 0 ? 'mt-2 pt-2 border-t border-slate-200' : ''}>
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <span className="text-xs text-slate-700 font-medium truncate">{fa.filename}</span>
                                  <span className="text-xs text-slate-500">{fa.status === 'processed' ? 'analyzed' : fa.status}{fa.processed_at ? ` · ${new Date(fa.processed_at).toLocaleDateString()}` : ''}</span>
                                </div>
                                {(fa.digest || fa.summary) && (
                                  <div className="mt-1.5 rounded-lg bg-white border border-slate-200 px-3 py-2">
                                    <DigestView text={String(fa.digest || fa.summary).slice(0, 1600)} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* DD request list */}
                <section>
                  <h4 className="font-semibold text-navy-900 mb-2 flex items-center gap-1.5"><FileSearch className="w-4 h-4 text-brand-600" /> Due diligence request list</h4>
                  {reportData?.requests ? (
                    <>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {Object.entries(reportData.requests.counts || {}).map(([k, v]) => (
                          <span key={k} className="text-xs bg-slate-100 rounded-full px-2.5 py-1 text-slate-600">{k}: <strong>{v}</strong></span>
                        ))}
                      </div>
                      {(reportData.requests.flagged || []).length > 0 && (
                        <ul className="space-y-1">
                          {reportData.requests.flagged.map((fr, i) => (
                            <li key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                              <AlertTriangle className="w-3 h-3 inline-block mr-1 -mt-0.5" />{fr.label}{fr.reason ? ` — ${fr.reason}` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-slate-400">No DD request list yet — add one in Deal Review.</p>
                  )}
                </section>

                {/* DD workstreams & findings snapshot */}
                <section>
                  <h4 className="font-semibold text-navy-900 mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-brand-600" /> DD workstreams &amp; findings</h4>
                  {reportData?.progress ? (
                    <>
                      <div className="text-xs text-slate-600 mb-2">
                        Workstream progress: <strong>{reportData.progress.percent ?? reportData.progress.percent_complete ?? '—'}%</strong>
                        {reportData.progress.completed_items != null && reportData.progress.total_items != null ? ` (${reportData.progress.completed_items}/${reportData.progress.total_items} items)` : ''}
                      </div>
                      {reportData.findingsText && (
                        <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-xs text-slate-700 whitespace-pre-line max-h-64 overflow-y-auto">
                          {reportData.findingsText}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-slate-400">No DD workspace yet — workstreams appear once diligence starts.</p>
                  )}
                </section>

                <p className="text-xs text-slate-400 pt-1 border-t border-slate-100">
                  This report is built strictly from the data you've provided — nothing is invented or assumed.
                </p>
              </>)}
            </div>
          </div>
        </div>
      )}

      {/* ── Merge another deal modal: pick ANY other deal to merge into this one ── */}
      {mergeListOpen && (
        <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !merging && setMergeListOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-bold text-navy-900">Merge another deal</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Pick a deal to merge into <strong>{deal?.companyName}</strong>. This deal keeps its details; the other entry fills any gaps (financials, notes, request lists, workstreams, documents) and is then removed.
                </p>
              </div>
              <button onClick={() => setMergeListOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-2">
              {mergeCandidates === null ? (
                <div className="flex items-center gap-2 text-slate-500 py-6 justify-center text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading deals…</div>
              ) : mergeCandidates.length === 0 ? (
                <p className="text-xs text-slate-400 py-4 text-center">No other deals to merge.</p>
              ) : mergeCandidates.map(d => (
                <div key={d.id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg px-3 py-2.5 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-medium text-navy-900 text-sm truncate">{d.companyName}</div>
                    <div className="text-xs text-slate-500">
                      Created {new Date(d.created_at).toLocaleDateString()}
                      {d.deal_phase ? ` · phase: ${d.deal_phase}` : ''}
                      {d.has_financials ? ' · has financial details' : ' · no financial details yet'}
                    </div>
                  </div>
                  <button
                    onClick={() => mergeDuplicate(d.id)}
                    disabled={merging}
                    className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 shrink-0"
                  >
                    <GitMerge className="w-3.5 h-3.5" /> {merging ? 'Merging…' : 'Merge into this deal'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// CaplyScoreBadge — the deal-quality index on each deal card.
// Deterministic + honest: score, coverage, criteria checks, gaps.
// ═══════════════════════════════════════════════════════════════

function CaplyScoreBadge({ dealId }) {
  const [score, setScore] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    let alive = true;
    api.get(`/api/intake/deals/${dealId}/score`)
      .then((r) => { if (alive) setScore(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [dealId]);

  if (!score) return null;
  const v = score.score;
  const ring = v >= 70 ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
    : v >= 40 ? 'border-gold-300 bg-gold-50 text-gold-700'
    : 'border-rose-300 bg-rose-50 text-rose-700';
  const label = { pursue: 'Pursue', mixed: 'Mixed', pass: 'Weak fit', needs_data: 'Needs data', insufficient_data: 'Needs data' }[score.verdict] || '';

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`inline-flex items-center gap-1.5 border rounded-full pl-2 pr-2.5 py-1 transition hover:shadow-sm ${ring}`}
        title="Caply Score — fit vs your thesis, deal economics, data completeness"
      >
        <span className="text-xs font-bold">{v == null ? '—' : v}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide opacity-80">Caply Score</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl z-20 p-3 text-left" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-navy-900">Caply Score: {v == null ? 'not computable yet' : `${v}/100 · ${label}`}</span>
            <span className="text-[10px] text-slate-400">{score.coverage_pct}% real data</span>
          </div>
          <p className="text-[11px] text-slate-600 mt-1.5 leading-relaxed">{score.recommendation}</p>
          <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
            {(score.criteria || []).map((c) => (
              <div key={c.name} className="flex items-start gap-1.5 text-[11px]">
                {c.ok === true ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />
                  : c.ok === false ? <XCircle className="w-3 h-3 text-rose-500 shrink-0 mt-0.5" />
                  : <HelpCircle className="w-3 h-3 text-slate-300 shrink-0 mt-0.5" />}
                <span className={c.ok === true ? 'text-slate-600' : c.ok === false ? 'text-slate-700 font-medium' : 'text-slate-400'}>
                  {c.name}
                  <span className="block text-[10px] text-slate-400 font-normal">{c.detail}</span>
                </span>
              </div>
            ))}
          </div>
          {(score.gaps || []).length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gold-700">Fill these gaps to sharpen the score</p>
              {(score.gaps || []).slice(0, 4).map((g) => (
                <p key={g.field} className="text-[11px] text-slate-600 mt-1 leading-snug">
                  <span className="font-semibold">{g.field.replace(/_/g, ' ')}:</span> {g.why} <span className="text-slate-400">({g.where})</span>
                </p>
              ))}
            </div>
          )}
          <p className="mt-2 text-[10px] text-slate-400 border-t border-slate-100 pt-1.5">{score.basis}</p>
        </div>
      )}
    </div>
  );
}



// PhaseFlow — compact deal-execution map with the current phase highlighted.
// Same flow the Deal Review shows, rendered under each Active Deals card.
function PhaseFlow({ dealId }) {
  const [phase, setPhase] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/api/dd/deals/${dealId}/phase`)
      .then(r => { if (!cancelled) setPhase(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dealId]);
  if (!phase) return null;
  const chips = phase.phases || [];
  return (
    <div className="flex items-center gap-1 flex-wrap mb-3 px-1">
      {chips.map((p, i) => (
        <React.Fragment key={p.id}>
          {i > 0 && <ChevronRight className="w-2.5 h-2.5 text-slate-200 flex-shrink-0" />}
          <span
            title={p.description}
            className={`text-[11px] px-1.5 py-0.5 rounded-full whitespace-nowrap border ${
              p.status === 'current' ? 'bg-brand-50 text-brand-700 border-brand-200 font-semibold'
              : p.status === 'done' ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
              : 'bg-slate-50 text-slate-400 border-slate-100'}`}
          >
            {p.label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}


// DealCard — grouped pipeline + report for a single deal
// ═══════════════════════════════════════════════════════════════

function DealCard({ pipelineReport, aiReport, onOpenPipeline, onOpenReport, onGenerateReport, onDealDelete, onConvert, isConverted, convertLoading }) {
  const companyName = pipelineReport?.portco_name || aiReport?.portco_name || 'Unknown Deal';
  const dealId = pipelineReport?.portco_id || aiReport?.portco_id;
  const [showDelete, setShowDelete] = useState(false);

  const pipelineStatus = pipelineReport?.status || 'draft';
  const hasReport = !!aiReport;
  const pipelineDone = pipelineStatus === 'completed';

  // Stage indicators
  const StageIcon = ({ done, label }) => (
    <div className="flex items-center gap-1">
      {done ? (
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
      ) : (
        <Clock className="w-3.5 h-3.5 text-slate-300" />
      )}
      <span className={`text-xs font-medium ${done ? 'text-emerald-600' : 'text-slate-400'}`}>{label}</span>
    </div>
  );

  return (
    <div className="card hover:border-brand-300 hover:shadow-cardHover transition group">
      {/* Header row */}
      <div className="flex items-center gap-3 mb-3">
        <div className="p-3 rounded-xl bg-slate-50 group-hover:bg-brand-50 transition shrink-0">
          <Building2 className="w-5 h-5 text-brand-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-navy-900">{companyName}</p>
          <p className="text-xs text-slate-400">{dealId}</p>
        </div>
        <CaplyScoreBadge dealId={dealId} />
        {showDelete ? (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <button onClick={() => onDealDelete(dealId)} className="text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 transition" title="Permanently deletes the deal, its files, documents, and reports">
              Delete permanently
            </button>
            <button onClick={() => setShowDelete(false)} className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition">
              Cancel
            </button>
          </div>
        ) : (
          <button onClick={e => { e.stopPropagation(); setShowDelete(true); }} className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition" title="Delete deal and all its data">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Stage flow */}
      <div className="flex items-center gap-3 mb-3 px-1">
        <StageIcon done={true} label="Deal" />
        <ChevronRight className="w-3 h-3 text-slate-200" />
        <StageIcon done={pipelineDone} label={pipelineDone ? 'Pipeline Done' : 'Pipeline'} />
        <ChevronRight className="w-3 h-3 text-slate-200" />
        <StageIcon done={hasReport} label={hasReport ? 'Report' : 'Report'} />
        <ChevronRight className="w-3 h-3 text-slate-200" />
        <StageIcon done={isConverted} label="Portco" />
      </div>

      {/* Deal execution map — current phase at a glance */}
      <PhaseFlow dealId={dealId} />

      {/* Summary text */}
      <p className="text-xs text-slate-500 mb-3 line-clamp-2">
        {pipelineReport?.executive_summary?.slice(0, 120) || 'No screening summary available.'}
      </p>

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={e => { e.stopPropagation(); onOpenPipeline(dealId); }} className="btn-secondary text-xs py-1.5 px-3">
          <Cpu className="w-3.5 h-3.5" />Pipeline
        </button>
        {hasReport ? (
          <button onClick={e => { e.stopPropagation(); onOpenReport(aiReport.report_id); }} className="btn-primary text-xs py-1.5 px-3">
            <FileText className="w-3.5 h-3.5" />View Report
          </button>
        ) : pipelineDone ? (
          <button onClick={e => { e.stopPropagation(); onGenerateReport(dealId); }} className="btn-primary text-xs py-1.5 px-3">
            <Sparkles className="w-3.5 h-3.5" />Generate Report
          </button>
        ) : (
          <span className="text-xs text-slate-400 italic">Upload documents to enable report</span>
        )}
        {hasReport && !isConverted && (
          <button
            onClick={e => { e.stopPropagation(); onConvert(dealId, aiReport.report_id); }}
            disabled={convertLoading === dealId}
            className="text-xs font-semibold text-brand-600 hover:text-brand-700 bg-brand-50 hover:bg-brand-100 px-3 py-1.5 rounded-lg transition flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {convertLoading === dealId ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" />Converting...</>
            ) : (
              <><Building2 className="w-3.5 h-3.5" />Convert to Portco</>
            )}
          </button>
        )}
        {isConverted && (
          <span className="badge bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="w-3 h-3 mr-1" />Portco
          </span>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// Main Reports Page
// ═══════════════════════════════════════════════════════════════

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [newReport, setNewReport] = useState({
    report_type: 'investment_memo',
    portco_id: 'portco-apex',
    period: 'Q3 2026',
  });
  const [generating, setGenerating] = useState(false);
  const [pipelineView, setPipelineView] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [convertedDeals, setConvertedDeals] = useState({});
  const [convertLoading, setConvertLoading] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  useEffect(() => {
    fetchReports();
    fetchDealStatuses();
    // Deep link: /reports?report=<id> opens that report directly
    // (e.g. a board deck generated from a portfolio company's page).
    const rid = new URLSearchParams(window.location.search).get('report');
    if (rid) setSelectedReport(rid);
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/reports');
      if (Array.isArray(res?.data) && res.data.length > 0) setReports(res.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchDealStatuses = async () => {
    try {
      const res = await api.get('/api/intake/deals');
      if (Array.isArray(res?.data)) {
        const converted = {};
        res.data.forEach(d => {
          if (d.converted_to_portco) converted[d.id] = d.converted_to_portco;
        });
        setConvertedDeals(converted);
      }
    } catch {}
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.post('/api/reports/generate', newReport);
      await fetchReports();
      setShowNewForm(false);
    } catch {} finally { setGenerating(false); }
  };

  const handleGoToReport = async (dealId) => {
    setGeneratingReport(true);
    try {
      const resp = await api.post(`/api/reports/${dealId}/generate-from-diligence`);
      if (resp?.data?.report_id) {
        await fetchReports();
        setPipelineView(null);
        setSelectedReport(resp.data.report_id);
      }
    } catch (err) {
      const existing = reports.find(r => r.portco_id === dealId && r.report_id?.startsWith('rpt-dil-'));
      if (existing) { setPipelineView(null); setSelectedReport(existing.report_id); }
    } finally { setGeneratingReport(false); }
  };

  const handleDownload = async (reportId) => {
    try {
      const res = await api.get(`/api/reports/${reportId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/html' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Caply_Report_${reportId}.html`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(`${api.defaults.baseURL}/api/reports/${reportId}/download`, '_blank');
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!dealId) return;
    try {
      setDeleteError(null);
      const res = await api.delete(`/api/intake/deals/${dealId}`);
      // The backend verifies zero-trace; surface anything it could not remove.
      const detail = res?.data?.detail || {};
      if (detail.failures?.length) {
        setDeleteError(`Some data could not be removed: ${detail.failures.join('; ')}`);
        setTimeout(() => setDeleteError(null), 6000);
      }
      await Promise.all([fetchReports(), fetchDealStatuses()]);
    } catch (err) {
      console.error('Failed to delete deal:', err);
      const d = err.response?.data?.detail;
      const msg = (d && typeof d === 'object' && (d.message || (d.failures || []).join('; ')))
        || (typeof d === 'string' && d)
        || 'Failed to delete deal';
      setDeleteError(msg);
      setTimeout(() => setDeleteError(null), 6000);
    }
  };

  const handleConvertToPortco = async (dealId) => {
    setConvertLoading(dealId);
    try {
      const res = await api.post(`/api/portfolio/deals/${dealId}/convert`);
      setConvertedDeals(prev => ({ ...prev, [dealId]: res.data?.portco_id || true }));
      await fetchDealStatuses();
      setDeleteError(null);
      setSuccessMsg(`✓ Converted to portfolio company: ${res.data?.portco_name || dealId}`);
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      setDeleteError(errorText(err, 'Failed to convert deal to portfolio company'));
      setTimeout(() => setDeleteError(null), 4000);
    } finally {
      setConvertLoading(null);
    }
  };

  // Group reports: deal-related ones are grouped by portco_id (dealId)
  const dealGroups = {};
  const standaloneReports = [];

  reports.forEach(r => {
    if (r.portco_id && r.portco_id.startsWith('deal-')) {
      if (!dealGroups[r.portco_id]) dealGroups[r.portco_id] = {};
      if (r.report_id && r.report_id.startsWith('rpt-dil-')) {
        dealGroups[r.portco_id].aiReport = r;
      } else {
        dealGroups[r.portco_id].pipelineReport = r;
      }
    } else {
      standaloneReports.push(r);
    }
  });

  // Apply filter to deal groups
  const filteredDeals = Object.entries(dealGroups).filter(([_, g]) => {
    if (filter === 'all') return true;
    if (filter === 'completed') return g.aiReport || g.pipelineReport?.status === 'completed';
    if (filter === 'in_progress') return g.pipelineReport?.status === 'in_progress';
    if (filter === 'draft') return g.pipelineReport?.status === 'draft' || (!g.pipelineReport);
    return true;
  });

  const filteredStandalone = filter === 'all' ? standaloneReports : standaloneReports.filter(r => r.status === filter);
  const totalCount = filteredDeals.length + filteredStandalone.length;

  // ── PIPELINE VIEW ──
  if (pipelineView) {
    return (
      <div className="page-container w-full max-w-7xl mx-auto">
        <button onClick={() => setPipelineView(null)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 transition mb-4">
          <ArrowLeft className="w-4 h-4" />Back to Active Deals
        </button>
        <div className="mb-4">
          <h2 className="page-title">Deal Review</h2>
          <p className="page-subtitle">Upload a deal’s documents — Caply reads them and pulls out what matters, in plain language</p>
        </div>
        <AgentPipeline dealId={pipelineView} onComplete={handleGoToReport} />
        {generatingReport && (
          <div className="card mt-4 flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-brand-500 animate-spin mr-2" />
            <span className="text-sm text-slate-600">Generating AI report from agent findings...</span>
          </div>
        )}
      </div>
    );
  }

  // ── DETAIL VIEW ──
  if (selectedReport) {
    const report = reports.find(r => r.report_id === selectedReport);
    // While the list is still loading, a deep-linked report may not be in
    // state yet — wait instead of clearing the selection.
    if (!report) {
      if (!loading) setSelectedReport(null);
      return null;
    }
    const dealId = report.portco_id;
    const isConverted = convertedDeals[dealId] ? true : false;
    return (
      <ReportDetail
        report={report}
        onBack={() => setSelectedReport(null)}
        onDownload={handleDownload}
        onViewPipeline={(d) => setPipelineView(d)}
        onConvertToPortco={() => handleConvertToPortco(dealId)}
        isConverted={isConverted}
        convertLoading={convertLoading === dealId}
      />
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="page-container">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="page-title">Active Deals</h2>
          <p className="page-subtitle">Investment memos, diligence summaries, and board-ready decks — built only from your data</p>
        </div>
        <button onClick={() => setShowNewForm(!showNewForm)} className="btn-primary">
          <Plus className="w-4 h-4" />Generate Report
        </button>
      </div>

      {showNewForm && (
        <div className="card animate-slide-up">
          <div className="card-header">
            <h3 className="card-title">Generate New Report</h3>
            <button onClick={() => setShowNewForm(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Report Type</label>
              <select value={newReport.report_type} onChange={e => setNewReport({ ...newReport, report_type: e.target.value })} className="form-select">
                {Object.entries(REPORT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Portfolio Company</label>
              <select value={newReport.portco_id} onChange={e => setNewReport({ ...newReport, portco_id: e.target.value })} className="form-select">
                <option value="portco-apex">Apex Software Systems</option>
                <option value="portco-nexus">Nexus Healthcare Solutions</option>
                <option value="portco-vanguard">Vanguard Logistics Automation</option>
                <option value="portco-lumina">Lumina Energy Analytics</option>
              </select>
            </div>
            <div>
              <label className="form-label">Period</label>
              <input type="text" value={newReport.period} onChange={e => setNewReport({ ...newReport, period: e.target.value })} className="form-input" />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={handleGenerate} disabled={generating} className="btn-primary disabled:opacity-50">
              {generating ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex items-center gap-2">
        {['all', 'completed', 'in_progress', 'draft'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
            filter === f ? 'bg-brand-600 text-navy-950' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
          }`}>
            {f === 'all' ? 'All Reports' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <span className="text-xs text-slate-400 ml-2">{totalCount} item{totalCount !== 1 ? 's' : ''}</span>
      </div>

      {deleteError && (
        <div className="card border-red-200 bg-red-50 mb-4">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {deleteError}
          </div>
        </div>
      )}
      {successMsg && (
        <div className="card border-emerald-200 bg-emerald-50 mb-4">
          <div className="flex items-center gap-2 text-sm text-emerald-700">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            {successMsg}
          </div>
        </div>
      )}

      {/* Reports list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : totalCount === 0 ? (
        <div className="card text-center py-12">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No reports yet</p>
          <p className="text-sm text-slate-400 mt-1">Submit a deal from the New Deal tab to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Deal groups — unified cards with pipeline + report */}
          {filteredDeals.map(([dealId, group]) => (
            <DealCard
              key={dealId}
              pipelineReport={group.pipelineReport}
              aiReport={group.aiReport}
              onOpenPipeline={(d) => setPipelineView(d)}
              onOpenReport={(reportId) => setSelectedReport(reportId)}
              onGenerateReport={(d) => handleGoToReport(d)}
              onDealDelete={handleDeleteDeal}
              onConvert={(d) => handleConvertToPortco(d)}
              isConverted={convertedDeals[dealId] ? true : false}
              convertLoading={convertLoading}
            />
          ))}

          {/* Divider between deals and standalone reports */}
          {filteredDeals.length > 0 && filteredStandalone.length > 0 && (
            <div className="flex items-center gap-3 py-3">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium">Portfolio Company Reports</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
          )}

          {/* Standalone reports (baseline portfolio company reports) */}
          {filteredStandalone.map(r => {
            const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.draft;
            const typeLabel = REPORT_TYPE_LABELS[r.report_type] || r.report_type;
            return (
              <div
                key={r.report_id}
                onClick={() => setSelectedReport(r.report_id)}
                className="card cursor-pointer hover:border-brand-300 hover:shadow-cardHover transition group"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-slate-50 group-hover:bg-brand-50 transition shrink-0">
                    <FileText className="w-6 h-6 text-slate-400 group-hover:text-brand-600 transition" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-bold text-navy-900">{typeLabel}</p>
                      <span className={`badge ${cfg.badge}`}>
                        <span className={`status-dot ${cfg.dot} mr-1`} />{cfg.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      {r.portco_name || r.portco_id} · {r.period || 'N/A'} · {r.executive_summary?.slice(0, 100) || 'No summary'}
                      {r.executive_summary?.length > 100 ? '...' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={e => { e.stopPropagation(); handleDownload(r.report_id); }} className="btn-secondary text-xs py-1.5 px-3">
                      <Download className="w-3.5 h-3.5" />Download
                    </button>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-brand-500 transition" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
